import { Plus, Power, RefreshCw, Server, X } from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { apiRequest } from "../api";
import { DataTable } from "../components/DataTable";
import { DetailDrawer } from "../components/DetailDrawer";
import { PageHeader } from "../components/PageHeader";
import { Pagination } from "../components/Pagination";
import type { CollectionResponse, DataRecord, Session } from "../types";

interface RentalModel {
  id: string;
  role: "video" | "encoder" | "decoder";
  bytes: number;
}

interface RentalOptions {
  defaults: {
    gpu: string;
    minimumVramGiB: number;
    minimumRamGiB: number;
    minimumDownloadMbps: number;
    minimumDiskGiB: number;
    minimumReliability: number;
    maximumPricePerDay: number;
  };
  gpuOptions: string[];
  models: RentalModel[];
  defaultModels: { video: string[]; encoders: string[]; decoders: string[] };
}

interface RentalForm {
  gpu: string;
  minimumVramGiB: number;
  minimumRamGiB: number;
  minimumDownloadMbps: number;
  maximumPricePerDay: number;
  modelIds: string[];
}

const FALLBACK_OPTIONS: RentalOptions = {
  defaults: {
    gpu: "NVIDIA GeForce RTX 5090",
    minimumVramGiB: 31,
    minimumRamGiB: 96,
    minimumDownloadMbps: 400,
    minimumDiskGiB: 500,
    minimumReliability: 0.99,
    maximumPricePerDay: 10,
  },
  gpuOptions: ["NVIDIA GeForce RTX 5090"],
  models: [
    { id: "minimax-h3-ref2va-pruned-bf16", role: "video", bytes: 40_225_724_176 },
    { id: "qwen3vl-32b-minimax-h3-bf16", role: "encoder", bytes: 51_506_295_256 },
    { id: "minimax-h3-video-vae-fp16", role: "decoder", bytes: 5_207_808_496 },
    { id: "minimax-h3-audio-vae-fp32", role: "decoder", bytes: 605_254_808 },
  ],
  defaultModels: {
    video: ["minimax-h3-ref2va-pruned-bf16"],
    encoders: ["qwen3vl-32b-minimax-h3-bf16"],
    decoders: ["minimax-h3-video-vae-fp16", "minimax-h3-audio-vae-fp32"],
  },
};

export function ServersPage({ session }: { session: Session }) {
  const [rows, setRows] = useState<DataRecord[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<DataRecord | null>(null);
  const [releaseTarget, setReleaseTarget] = useState<DataRecord | null>(null);
  const [rental, setRental] = useState<DataRecord | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [rentOpen, setRentOpen] = useState(false);
  const [renting, setRenting] = useState(false);
  const [options, setOptions] = useState<RentalOptions>(FALLBACK_OPTIONS);
  const [rentForm, setRentForm] = useState<RentalForm>(() => formFromOptions(FALLBACK_OPTIONS));
  const pageSize = 10;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiRequest<CollectionResponse>(`/api/comfy/servers?page=${page}&pageSize=${pageSize}&active=true`);
      setRows(Array.isArray(response.servers) ? response.servers.filter(isRecord) : []);
      setTotal(typeof response.total === "number" ? response.total : 0);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not load servers");
    } finally { setLoading(false); }
  }, [page]);
  useEffect(() => { void load(); }, [load]);

  async function openRental() {
    setRentOpen(true);
    setError("");
    try {
      const response = await apiRequest<RentalOptions>("/api/comfy/rental-options");
      setOptions(response);
      setRentForm(formFromOptions(response));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not load rental options");
    }
  }

  async function submitRental(event: FormEvent) {
    event.preventDefault();
    setRenting(true);
    setError("");
    try {
      const result = await apiRequest<DataRecord>("/api/comfy/servers/rent", {
        method: "POST",
        body: JSON.stringify({
          gpu: rentForm.gpu,
          minimumVramGiB: rentForm.minimumVramGiB,
          minimumRamGiB: rentForm.minimumRamGiB,
          minimumDownloadMbps: rentForm.minimumDownloadMbps,
          maximumPricePerDay: rentForm.maximumPricePerDay,
          models: modelPayload(options.models, rentForm.modelIds),
        }),
      }, session.csrfToken);
      setSelected(result);
      setRentOpen(false);
      await pollOperation(String(result.operationId ?? ""));
      await load();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not rent a server");
    } finally { setRenting(false); }
  }

  async function prepareRelease(row: DataRecord) {
    setReleaseTarget(row);
    setRental(null);
    try {
      const response = await apiRequest<CollectionResponse>(`/api/comfy/rental-orders?serverId=${encodeURIComponent(String(row.serverId))}&pageSize=10`);
      const orders = Array.isArray(response.rentalOrders) ? response.rentalOrders.filter(isRecord) : [];
      setRental(orders.find((order) => order.orderId === row.orderId) ?? orders[0] ?? null);
    } catch {
      setRental(null);
    }
  }

  async function confirmRelease() {
    if (!releaseTarget) return;
    setReleasing(true);
    setError("");
    try {
      const result = await apiRequest<DataRecord>(`/api/comfy/servers/${releaseTarget.serverId}`, { method: "DELETE" }, session.csrfToken);
      setSelected(result);
      setReleaseTarget(null);
      await pollOperation(String(result.operationId ?? ""));
      await load();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not release server");
    } finally { setReleasing(false); }
  }

  async function pollOperation(operationId: string) {
    if (!operationId) return;
    for (let attempt = 0; attempt < 720; attempt += 1) {
      const result = await apiRequest<{ operation: DataRecord }>(`/api/comfy/operations/${operationId}`);
      setSelected(result.operation);
      if (["succeeded", "failed"].includes(String(result.operation.status))) return;
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }
  }

  function toggleModel(modelId: string) {
    setRentForm((current) => ({
      ...current,
      modelIds: current.modelIds.includes(modelId)
        ? current.modelIds.filter((id) => id !== modelId)
        : [...current.modelIds, modelId],
    }));
  }

  return <>
    <PageHeader
      title="Servers"
      description="Active ComfyUI hosts, agent health, queues, rental, and release state."
      onRefresh={() => void load()}
      refreshing={loading}
      actions={<button className="primary-button" type="button" onClick={() => void openRental()}><Plus size={16} />Rent server</button>}
    />
    {error ? <div className="error-banner">{error}</div> : null}
    <section className="data-surface">
      {loading && rows.length === 0 ? <div className="loading-state">Loading servers...</div> : <DataTable rows={rows} columns={[{ key: "serverId", label: "Server" }, { key: "status", kind: "status" }, { key: "orderId", label: "Order" }, { key: "agentConnected", label: "Agent" }, { key: "queue" }, { key: "lastHeartbeatAt", label: "Heartbeat", kind: "date" }]} rowKey={(row) => String(row.serverId)} onInspect={setSelected} actions={(row) => canRelease(row) ? <button className="danger-action" type="button" onClick={() => void prepareRelease(row)}><Power size={15} />Release</button> : null} />}
      <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
    </section>
    <DetailDrawer title="Server or operation" value={selected} onClose={() => setSelected(null)} />

    {rentOpen ? <div className="modal-backdrop"><section className="confirm-modal rent-server-modal" role="dialog" aria-modal="true" aria-labelledby="rent-title"><header><div><p className="eyebrow">Clore marketplace</p><h2 id="rent-title">Rent a ComfyUI server</h2></div><button className="icon-button" type="button" onClick={() => setRentOpen(false)} title="Cancel rental"><X size={18} /></button></header><form onSubmit={(event) => void submitRental(event)}><div className="rent-form-grid"><label><span>GPU</span><select aria-label="GPU" value={rentForm.gpu} onChange={(event) => setRentForm((current) => ({ ...current, gpu: event.target.value }))}>{options.gpuOptions.map((gpu) => <option key={gpu} value={gpu}>{gpu}</option>)}</select></label><label><span>Minimum VRAM (GiB)</span><input aria-label="Minimum VRAM (GiB)" type="number" min={8} value={rentForm.minimumVramGiB} onChange={(event) => setRentForm((current) => ({ ...current, minimumVramGiB: Number(event.target.value) }))} /></label><label><span>Minimum RAM (GiB)</span><input aria-label="Minimum RAM (GiB)" type="number" min={16} value={rentForm.minimumRamGiB} onChange={(event) => setRentForm((current) => ({ ...current, minimumRamGiB: Number(event.target.value) }))} /></label><label><span>Minimum download (Mbps)</span><input aria-label="Minimum download (Mbps)" type="number" min={0} value={rentForm.minimumDownloadMbps} onChange={(event) => setRentForm((current) => ({ ...current, minimumDownloadMbps: Number(event.target.value) }))} /></label><label><span>Maximum price (USD/day)</span><input aria-label="Maximum price (USD/day)" type="number" min={0.01} max={100} step={0.01} value={rentForm.maximumPricePerDay} onChange={(event) => setRentForm((current) => ({ ...current, maximumPricePerDay: Number(event.target.value) }))} /></label></div><fieldset className="model-selection"><legend>Models</legend>{options.models.map((model) => <label key={model.id}><input type="checkbox" checked={rentForm.modelIds.includes(model.id)} onChange={() => toggleModel(model.id)} /><span><strong>{model.id}</strong><small>{model.role} - {formatGiB(model.bytes)} GiB</small></span></label>)}</fieldset><div className="rental-safeguards"><Server size={17} /><p>Selection also requires {options.defaults.minimumDiskGiB} GiB disk, {options.defaults.minimumReliability} reliability, and historical download above {rentForm.minimumDownloadMbps} Mbps. The selected ${rentForm.maximumPricePerDay}/day rate is locked into the order.</p></div><footer><button className="secondary-button" type="button" onClick={() => setRentOpen(false)}>Cancel</button><button className="primary-button" type="submit" disabled={renting || rentForm.modelIds.length === 0}>{renting ? <RefreshCw size={16} className="spin" /> : <Plus size={16} />}{renting ? "Searching..." : "Find and rent"}</button></footer></form></section></div> : null}

    {releaseTarget ? <div className="modal-backdrop"><section className="confirm-modal" role="dialog" aria-modal="true" aria-labelledby="release-title"><header><div><p className="eyebrow">Destructive operation</p><h2 id="release-title">Release server {String(releaseTarget.serverId)}</h2></div><button className="icon-button" type="button" onClick={() => setReleaseTarget(null)} title="Cancel release"><X size={18} /></button></header><dl><div><dt>Order</dt><dd>{String(releaseTarget.orderId)}</dd></div><div><dt>Status</dt><dd>{String(releaseTarget.status)}</dd></div><div><dt>Rental rate</dt><dd>{typeof rental?.pricePerDay === "number" ? `$${rental.pricePerDay.toFixed(2)}/day` : "Unavailable"}</dd></div></dl><footer><button className="secondary-button" type="button" onClick={() => setReleaseTarget(null)}>Cancel</button><button className="danger-button" type="button" onClick={() => void confirmRelease()} disabled={releasing}>{releasing ? <RefreshCw size={16} className="spin" /> : <Power size={16} />}Release server</button></footer></section></div> : null}
  </>;
}

function formFromOptions(options: RentalOptions): RentalForm {
  return {
    gpu: options.defaults.gpu,
    minimumVramGiB: options.defaults.minimumVramGiB,
    minimumRamGiB: options.defaults.minimumRamGiB,
    minimumDownloadMbps: options.defaults.minimumDownloadMbps,
    maximumPricePerDay: options.defaults.maximumPricePerDay,
    modelIds: [...options.defaultModels.video, ...options.defaultModels.encoders, ...options.defaultModels.decoders],
  };
}

function modelPayload(models: RentalModel[], selected: string[]): Record<string, string[]> {
  return {
    video: models.filter((model) => model.role === "video" && selected.includes(model.id)).map((model) => model.id),
    encoders: models.filter((model) => model.role === "encoder" && selected.includes(model.id)).map((model) => model.id),
    decoders: models.filter((model) => model.role === "decoder" && selected.includes(model.id)).map((model) => model.id),
  };
}

function formatGiB(bytes: number): string { return (bytes / 1024 ** 3).toFixed(1); }
function canRelease(row: DataRecord): boolean { return Boolean(row.orderId) && !["released", "releasing"].includes(String(row.status)); }
function isRecord(value: unknown): value is DataRecord { return Boolean(value) && typeof value === "object" && !Array.isArray(value); }
